"use client";

import React from "react";
import { Dumbbell, Activity, Shield, Flame, CheckCircle2, UserCheck } from "lucide-react";
import { GYM_FACILITIES, GYM_COACHES } from "@/lib/demos/gymData";

interface FacilityShowcaseSectionProps {
  onOpenTrialPass: () => void;
}

export default function FacilityShowcaseSection({
  onOpenTrialPass,
}: FacilityShowcaseSectionProps) {
  return (
    <section className="py-16 sm:py-24 bg-stone-950 text-white border-b border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        {/* Facilities Header */}
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-bold text-emerald-400 uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5" />
            <span>Club Specifications</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-white">
            Designed for Uncompromising Performance
          </h2>

          <p className="text-xs sm:text-sm text-stone-400">
            From competition platforms to cold-plunge recovery tubs, every square foot is optimized for athletic longevity.
          </p>
        </div>

        {/* 3 Facility Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {GYM_FACILITIES.map((fac) => (
            <div
              key={fac.id}
              className="p-7 rounded-3xl bg-stone-900/60 border border-stone-800 space-y-5 hover:border-amber-500/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-md bg-stone-800 text-stone-300">
                  {fac.category}
                </span>
                <span className="text-[11px] text-stone-400">{fac.hours}</span>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2">{fac.name}</h3>
                <p className="text-xs text-stone-400 leading-relaxed">
                  {fac.description}
                </p>
              </div>

              <div className="pt-3 border-t border-stone-800 space-y-2">
                <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block">
                  Equipment Specifications:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {fac.specs.map((s, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 rounded bg-stone-950 border border-stone-800 text-[10px] text-stone-300 font-medium"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Master Coach Faculty Roster */}
        <div className="pt-10 space-y-10">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
              Faculty &amp; Sports Science
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
              Certified Master Performance Coaches
            </h3>
            <p className="text-xs text-stone-400">
              Each new member is paired with a specialist aligned with their biometric profile.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {GYM_COACHES.map((coach) => (
              <div
                key={coach.id}
                className="p-5 rounded-2xl bg-stone-900/40 border border-stone-800/80 space-y-3 hover:border-stone-700 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white">{coach.name}</h4>
                  <p className="text-[11px] text-amber-400 font-medium">{coach.title}</p>
                </div>
                <div className="text-[11px] text-stone-300 space-y-1 pt-1 border-t border-stone-800/60">
                  <p><strong className="text-stone-400">Specialty:</strong> {coach.specialty}</p>
                  <p className="text-[10px] text-stone-400">{coach.certifications}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Bar */}
          <div className="text-center pt-4">
            <button
              onClick={onOpenTrialPass}
              className="py-3 px-8 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 font-black text-xs uppercase tracking-wide transition-all shadow-lg shadow-amber-500/20 hover:scale-[1.02]"
            >
              Book Coach Orientation &amp; VIP Pass
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
